<template>
  <div class="upload-wrapper">
    <div class="upload-card">
      <div class="header">
        <h1>동영상 업로드</h1>
        <button @click="handleLogout" class="logout-btn">로그아웃</button>
      </div>
      
      <div 
        class="drop-zone" 
        @dragover.prevent 
        @drop.prevent="handleDrop"
        @click="triggerFileInput"
        :class="{ 'dragging': isDragging }"
        @dragenter="isDragging = true"
        @dragleave="isDragging = false"
      >
        <div v-if="!file" class="placeholder">
          <svg class="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p>동영상을 이곳에 끌어다 놓거나 클릭하여 선택하세요</p>
        </div>
        <div v-else class="file-info">
          <svg class="file-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
             <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
             <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>{{ file.name }}</p>
          <p class="file-size">{{ formatSize(file.size) }}</p>
        </div>
        <input 
          type="file" 
          ref="fileInput" 
          class="hidden-input" 
          accept="video/*" 
          @change="handleFileSelect"
        >
      </div>
      
      <div v-if="file" class="file-type-selector">
          <label>
              <input type="radio" v-model="uploadFileType" :value="1"> 동영상
          </label>
      </div>
      
      <div v-if="file" class="actions">
        <button class="upload-btn" @click="uploadFile" :disabled="isUploading">
          <span v-if="!isUploading">업로드 시작</span>
          <span v-else>업로드 중 {{ uploadProgress }}%</span>
        </button>
        <button class="cancel-btn" @click="file = null" :disabled="isUploading">취소</button>
      </div>

      <div v-if="uploadStatus" :class="['status-msg', uploadStatus.type]">
        {{ uploadStatus.message }}
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import SparkMD5 from 'spark-md5';
import { _baseURL } from '@/config/api';

export default {
  name: 'UploadPage',
  data() {
    return {
      isDragging: false,
      file: null,
      isUploading: false,
      uploadProgress: 0,
      uploadStatus: null,
      chunkSize: 1024 * 1024 * 4, // 4MB
      uploadFileType: 1 // 1=video, 2=advertisement (Default: Video)
    }
  },
  methods: {
    handleLogout() {
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('useridx');
      this.$router.push('/');
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      if (event.target.files.length > 0) {
        this.file = event.target.files[0];
        this.uploadStatus = null;
      }
    },
    handleDrop(event) {
      this.isDragging = false;
      const droppedFiles = event.dataTransfer.files;
      
      if (droppedFiles.length > 1) {
        alert('파일은 1개만 업로드할 수 있습니다.');
        return;
      }

      if (droppedFiles.length > 0) {
        const file = droppedFiles[0];
        // Check for video type
        if (!file.type.startsWith('video/')) {
          alert('동영상 파일만 업로드할 수 있습니다.');
          return;
        }
        
        this.file = file;
        this.uploadStatus = null;
        // Default to video type when dropping
        this.uploadFileType = 1;
      }
    },
    formatSize(size) {
      if (size === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(size) / Math.log(k));
      return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    async calculateHash(chunk) {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        const spark = new SparkMD5.ArrayBuffer();

        fileReader.onload = (e) => {
          spark.append(e.target.result);
          resolve(spark.end());
        };

        fileReader.onerror = (e) => {
          reject(e);
        };

        fileReader.readAsArrayBuffer(chunk);
      });
    },
    async uploadFile() {
        if (!this.file) return;

        const file = this.file;
        const chunkCount = Math.ceil(file.size / this.chunkSize);
        const uuid = uuidv4();
        let success = false;

        this.isUploading = true;
        this.uploadProgress = 0;
        this.uploadStatus = { type: 'info', message: '업로드 중...' };

        for (let i = 0; i < chunkCount; i++) {
            success = false;
            const chunk = file.slice(i * this.chunkSize, (i + 1) * this.chunkSize);
            
            // Calculate Hash
            let hash = '';
            try {
                hash = await this.calculateHash(chunk);
            } catch (err) {
                console.error('Hash calculation failed:', err);
                this.uploadStatus = { type: 'error', message: '파일 처리 실패.' };
                this.isUploading = false;
                return;
            }

            let retry = 0;
            while (!success && retry < 3) {
                try {
                    const formData = new FormData();
                    formData.append('file', chunk);
                    formData.append('uuid', uuid);
                    formData.append('idx', i);
                    formData.append('size', file.size);
                    formData.append('hash', hash);                    

                    await axios.post(`${_baseURL}Upload/chunkuploadcenter`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    
                    success = true;
                    // Update progress
                    this.uploadProgress = Math.round(((i + 1) / chunkCount) * 100);
                } catch (err) {
                    console.error('Upload failed:', err);
                    retry++;
                    if (retry >= 3) {
                         this.uploadStatus = { type: 'error', message: '업로드 실패. 잠시 후 다시 시도해주세요.' };
                         this.isUploading = false;
                         return;
                    }
                }
            }
        }

        if (success) {
            try {
                const centeridx = parseInt(localStorage.getItem('centeridx'));
                if (!centeridx) {
                     throw new Error('사용자 인증 실패');
                }

                await axios.post(`${_baseURL}Upload/completecenter`, {
                    uuid: uuid,
                    filename: file.name,                                      
                    centeridx: centeridx,
                    cnt: chunkCount,  
                });

                // Reset
                this.file = null;
                alert('업로드가 완료되었습니다. 파일 처리가 진행 중입니다.');
                this.uploadStatus = { type: 'success', message: '업로드가 완료되었습니다!' };
                
                // Placeholder for fetchLibraryList
                // await this.fetchLibraryList(); 

            } catch (err) {
                console.error('Complete upload failed:', err);
                this.uploadStatus = { type: 'error', message: '업로드 완료 처리 실패.' };
            } finally {
                this.isUploading = false;
            }
        } else {
            this.isUploading = false;
        }
    }
  }
}
</script>

<style scoped>
.upload-wrapper {
  width: 100%;
  height: 100%;
  padding: 2rem;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
}

.upload-card {
  background: var(--surface-dark);
  padding: 2rem;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  height: fit-content;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header h1 {
  margin: 0;
  font-size: 1.5rem;
}

.logout-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: var(--text-muted);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--text-light);
}

.drop-zone {
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 3rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: rgba(255, 255, 255, 0.02);
}

.drop-zone:hover, .drop-zone.dragging {
  border-color: var(--primary-color);
  background: rgba(99, 102, 241, 0.1);
}

.upload-icon, .file-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 1rem;
  color: var(--text-muted);
}

.file-info p {
  margin: 0.25rem 0;
}

.file-size {
  color: var(--text-muted);
  font-size: 0.875rem;
}

.hidden-input {
  display: none;
}

.file-type-selector {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  gap: 1.5rem;
}

.file-type-selector label {
  cursor: pointer;
  color: var(--text-light);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.actions {
  margin-top: 1.5rem;
  display: flex;
  gap: 1rem;
}

.upload-btn {
  flex: 1;
  padding: 0.875rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.upload-btn:disabled {
  opacity: 0.7;
}

.cancel-btn {
  padding: 0.875rem 2rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--text-light);
  border-radius: 8px;
  cursor: pointer;
}

.status-msg {
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
}

.status-msg.success {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
}

.status-msg.error {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}
</style>
